import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import eventService from '../../services/eventService';
import { usePermissions } from '../../context/PermissionContext';
import LockedAction from '../../components/PermissionLock';

const STATUS_BADGE = { draft: 'secondary', active: 'success', closed: 'dark' };

function EventFormModal({ show, onClose, onSaved, event }) {
  const blank = { name: '', startDate: '', endDate: '', entryFee: '', concessionsCommissionPct: '', entranceCommissionPerUnit: '', status: 'draft', notes: '', venueAddress: '', venueLat: '', venueLng: '', geoRadius: '300' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pinning, setPinning] = useState(false);
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    if (show) {
      setForm(event ? {
        name: event.name || '',
        startDate: event.startDate || '',
        endDate: event.endDate || '',
        entryFee: event.entryFee ?? '',
        concessionsCommissionPct: event.concessionsCommissionPct ?? '',
        entranceCommissionPerUnit: event.entranceCommissionPerUnit ?? '',
        status: event.status || 'draft',
        notes: event.notes || '',
        venueAddress: event.venueAddress || '',
        venueLat: event.venueLat != null ? String(event.venueLat) : '',
        venueLng: event.venueLng != null ? String(event.venueLng) : '',
        geoRadius: event.geoRadius != null ? String(event.geoRadius) : '300',
      } : blank);
      setError('');
      setPinError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, event]);

  const pinMyLocation = () => {
    if (!navigator.geolocation) { setPinError('Geolocation not supported by this browser.'); return; }
    setPinning(true); setPinError('');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(f => ({ ...f, venueLat: String(pos.coords.latitude.toFixed(7)), venueLng: String(pos.coords.longitude.toFixed(7)) }));
        setPinning(false);
      },
      () => { setPinError('Could not get location. Check browser permissions.'); setPinning(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.startDate || !form.endDate) { setError('Name, start date and end date are required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name: form.name.trim(),
        startDate: form.startDate,
        endDate: form.endDate,
        entryFee: form.entryFee !== '' ? parseFloat(form.entryFee) : null,
        concessionsCommissionPct: form.concessionsCommissionPct !== '' ? parseFloat(form.concessionsCommissionPct) : null,
        entranceCommissionPerUnit: form.entranceCommissionPerUnit !== '' ? parseFloat(form.entranceCommissionPerUnit) : null,
        status: form.status,
        notes: form.notes.trim() || null,
        venueAddress: form.venueAddress.trim() || null,
        venueLat: form.venueLat !== '' ? parseFloat(form.venueLat) : null,
        venueLng: form.venueLng !== '' ? parseFloat(form.venueLng) : null,
        geoRadius: form.geoRadius !== '' ? parseInt(form.geoRadius) : 300,
      };
      const saved = event ? await eventService.updateEvent(event.id, payload) : await eventService.createEvent(payload);
      onSaved(saved);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save event.');
      setSaving(false);
    }
  };

  if (!show) return null;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 540 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{event ? 'Edit Event' : 'New Event'}</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}

              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Event Name <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. FIFA Fun Fest Miami" autoFocus />
              </div>

              <div className="row g-2 mb-3">
                <div className="col">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Start Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control form-control-sm" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
                </div>
                <div className="col">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>End Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control form-control-sm" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
                </div>
              </div>

              <div className="mb-3 p-2 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <div className="fw-semibold mb-2" style={{ fontSize: 12 }}>Financials</div>
                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Entry Fee ($) <span className="text-muted fw-normal">one-time venue cost</span></label>
                    <input type="number" min="0" step="0.01" className="form-control form-control-sm" value={form.entryFee} onChange={e => set('entryFee', e.target.value)} placeholder="e.g. 10000" />
                  </div>
                  <div className="col-6">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Concessions commission (%)</label>
                    <input type="number" min="0" max="100" step="0.1" className="form-control form-control-sm" value={form.concessionsCommissionPct} onChange={e => set('concessionsCommissionPct', e.target.value)} placeholder="e.g. 40" />
                  </div>
                  <div className="col-6">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Entrance commission ($/unit)</label>
                    <input type="number" min="0" step="0.01" className="form-control form-control-sm" value={form.entranceCommissionPerUnit} onChange={e => set('entranceCommissionPerUnit', e.target.value)} placeholder="e.g. 1.00" />
                  </div>
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Status</label>
                  <select className="form-select form-select-sm" value={form.status} onChange={e => set('status', e.target.value)}>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              <div className="mb-3 p-2 rounded" style={{ background: '#f0f7ff', border: '1px solid #cfe2ff' }}>
                <div className="fw-semibold mb-2" style={{ fontSize: 12 }}>Venue &amp; Geolocation</div>
                <div className="text-muted mb-2" style={{ fontSize: 11 }}>
                  Set the event location so employees can clock in from their phones. They must be within the radius to punch.
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>Venue Address</label>
                  <input type="text" className="form-control form-control-sm" value={form.venueAddress} onChange={e => set('venueAddress', e.target.value)} placeholder="e.g. 1 Stadium Dr, Miami, FL" />
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-5">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Latitude</label>
                    <input type="number" step="any" className="form-control form-control-sm" value={form.venueLat} onChange={e => set('venueLat', e.target.value)} placeholder="e.g. 25.7617" />
                  </div>
                  <div className="col-5">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Longitude</label>
                    <input type="number" step="any" className="form-control form-control-sm" value={form.venueLng} onChange={e => set('venueLng', e.target.value)} placeholder="e.g. -80.1918" />
                  </div>
                  <div className="col-2 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary w-100"
                      style={{ fontSize: 11 }}
                      onClick={pinMyLocation}
                      disabled={pinning}
                      title="Capture your current GPS coordinates"
                    >
                      {pinning ? '…' : 'Pin'}
                    </button>
                  </div>
                </div>
                {pinError && <div className="text-danger mb-1" style={{ fontSize: 11 }}>{pinError}</div>}
                {form.venueLat && form.venueLng && (
                  <div className="text-success mb-2" style={{ fontSize: 11 }}>
                    ✓ Location set: {parseFloat(form.venueLat).toFixed(5)}, {parseFloat(form.venueLng).toFixed(5)}
                  </div>
                )}
                <div>
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>
                    Geo-fence Radius: <strong>{form.geoRadius} m</strong>
                    <span className="text-muted fw-normal ms-1">(employees must be within this distance)</span>
                  </label>
                  <input
                    type="range" className="form-range" min="50" max="1000" step="25"
                    value={form.geoRadius}
                    onChange={e => set('geoRadius', e.target.value)}
                  />
                  <div className="d-flex justify-content-between text-muted" style={{ fontSize: 10 }}>
                    <span>50 m (tight)</span><span>300 m (default)</span><span>1 km (loose)</span>
                  </div>
                </div>
              </div>

              <div className="mb-2">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Notes</label>
                <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : (event ? 'Save Changes' : 'Create Event')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function EventsPage() {
  const navigate = useNavigate();
  const { role } = usePermissions();
  const isManager = role === 'event_manager';
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  useEffect(() => {
    eventService.listEvents()
      .then(list => {
        setEvents(list);
        // A third-party manager usually has exactly one event — go straight to it.
        if (isManager && list.length === 1) navigate(`/events/${list[0].id}`, { replace: true });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaved = (saved) => {
    setEvents(prev => {
      const idx = prev.findIndex(e => e.id === saved.id);
      return idx >= 0 ? prev.map(e => e.id === saved.id ? saved : e) : [saved, ...prev];
    });
    setShowModal(false);
    setEditingEvent(null);
    navigate(`/events/${saved.id}`);
  };

  const durationDays = (e) => {
    const diff = (new Date(e.endDate) - new Date(e.startDate)) / 86400000;
    return Math.round(diff) + 1;
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 860 }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">Events</h4>
        {isManager ? (
          <LockedAction className="btn btn-sm btn-outline-secondary" what="create events">New Event</LockedAction>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={() => { setEditingEvent(null); setShowModal(true); }}>
            + New Event
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5"><span className="spinner-border" /></div>
      ) : events.length === 0 ? (
        <div className="text-center text-muted py-5">
          <p className="mb-2">No events yet.</p>
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowModal(true)}>Create your first event</button>
        </div>
      ) : (
        <div className="list-group">
          {events.map(ev => (
            <button key={ev.id} className="list-group-item list-group-item-action d-flex align-items-center gap-3 py-3"
              onClick={() => navigate(`/events/${ev.id}`)}>
              <div className="flex-grow-1">
                <div className="fw-semibold">{ev.name}</div>
                <div className="text-muted small">
                  {ev.startDate} → {ev.endDate} &nbsp;·&nbsp; {durationDays(ev)} days
                </div>
              </div>
              <span className={`badge bg-${STATUS_BADGE[ev.status] || 'secondary'} text-capitalize`}>{ev.status}</span>
            </button>
          ))}
        </div>
      )}

      <EventFormModal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingEvent(null); }}
        onSaved={handleSaved}
        event={editingEvent}
      />
    </div>
  );
}
